import { coreClient } from '@/api/coreclient'
import type {
  NewUser,
  OrganizationAttributes,
  UserAttributes,
  NewClient,
  DebugProperty,
  Subscription,
  AdminUser,
  SimpleStripeUser,
  AdminApiUsage,
  PropertyReportSubscriptionAttributes,
  CreateGroupAttributes,
  UpdateGroupAttributes,
  TemplateAIAttributes,
  DatasetPayload,
  AdminInPortfolioStats
} from 'common'

export default {
  async getAllUsers() {
    const client = await coreClient()
    return client.admin
      .getAllUsers()
      .then((data) => {
        return data.data
      })
      .catch((e) => {
        console.error(e)
        return [] as AdminUser[]
      })
  },
  async getAllSimpleStripeUsers() {
    const client = await coreClient()
    return client.admin
      .getAllSimpleStripeUsers()
      .then((data) => {
        return data.data
      })
      .catch((e) => {
        console.error(e)
        return [] as SimpleStripeUser[]
      })
  },
  async getOrganizationsByProductId(productId?: string) {
    const client = await coreClient()
    return client.admin
      .getOrganizationsByProductId(productId)
      .then((response) => response.data)
      .catch((e) => {
        console.error(e)
        return [] as OrganizationAttributes[]
      })
  },
  async createNewUser(data: NewUser) {
    const client = await coreClient()
    return client.admin
      .createNewUser(data)
      .then((data) => {
        return data.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async createNewClient(data: NewClient) {
    const client = await coreClient()
    return client.admin
      .createNewClient(data)
      .then((data) => {
        return data.data
      })
      .catch((e) => {
        console.error(e)
        return false
      })
  },
  async listOrganizationMembers(userId: string) {
    const client = await coreClient()
    return client.admin
      .listOrganizationMembers(userId)
      .then((data) => {
        return data.data
      })
      .catch((e) => {
        console.error(e)
        return [] as UserAttributes[]
      })
  },
  async removeFromOrganization(userId: string, toRemoveUserId: string, toTransferUserId?: string) {
    const client = await coreClient()
    return client.admin
      .removeFromOrganization(userId, {
        user_id: toRemoveUserId,
        transfer_user_id: toTransferUserId
      })
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async getOrganization(userId: string) {
    const client = await coreClient()
    return client.admin
      .getOrganization(userId)
      .then((data) => {
        return data.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async updateOrganization(userId: string, id: number, org: Omit<OrganizationAttributes, 'id'>) {
    const client = await coreClient()
    return client.admin
      .patchOrganization(userId, id, org)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async invitePeople(userId: string, emails: string[]) {
    const client = await coreClient()
    return client.admin
      .invitePeople(userId, { emails })
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async resendInviteEmail(userId: string, email: string) {
    const client = await coreClient()
    return client.admin
      .resendInviteEmail(userId, email)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async copyPropertyReport(reportId: number) {
    const client = await coreClient()
    return client.admin
      .copyPropertyReport(reportId)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async copyMarketAnalytics(reportId: number) {
    const client = await coreClient()
    return client.admin
      .copyMarketAnalytics(reportId)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async copyDataset(datasetId: number) {
    const client = await coreClient()
    return client.admin
      .copyDataset(datasetId)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async copyRentRoll(rentRollId: number) {
    const client = await coreClient()
    return client.admin
      .copyRentRoll(rentRollId)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async extendLastSub(userId: string, extension: number) {
    const client = await coreClient()
    return client.admin
      .extendLastSub(userId, extension)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async updateTrialEnd(subscriptionId: string, trialEnd: string) {
    const client = await coreClient()
    return client.admin
      .updateTrialEnd(subscriptionId, trialEnd)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async debugProperties(ids: string[]) {
    const client = await coreClient()
    return client.admin
      .debugProperties({ ids })
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return [] as DebugProperty[]
      })
  },
  async getCustomerSubscriptions(userId: string) {
    const client = await coreClient()
    return client.admin
      .getCustomerSubscriptions(userId)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return [] as Subscription[]
      })
  },
  async getChildSubscriptions(userId: string) {
    const client = await coreClient()
    return client.admin
      .getChildrenSubscriptions(userId)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return [] as Subscription[]
      })
  },
  async closeChildAccount(userId: string, subscriptionId: string) {
    const client = await coreClient()
    return client.admin
      .closeChildAccount(userId, subscriptionId)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async keepChildAccount(userId: string, subscriptionId: string) {
    const client = await coreClient()
    return client.admin
      .keepChildAccount(userId, subscriptionId)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async getApiUsage(days: number) {
    const client = await coreClient()
    return client.admin
      .getApiUsage(days)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return [] as AdminApiUsage[]
      })
  },
  async getInPortfolioStats() {
    const client = await coreClient()
    return client.admin
      .getInPortfolioStats()
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return [] as AdminInPortfolioStats[]
      })
  },
  async listOrganizationReports(organizationId: number, userId: string) {
    const client = await coreClient()
    return client.admin
      .listOrganizationReports(organizationId, userId)
      .then((data) => {
        return data.data
      })
      .catch((e) => {
        console.error(e)
        return [] as PropertyReportSubscriptionAttributes[]
      })
  },
  async toggleOrganizationPause(id: number) {
    const client = await coreClient()
    return client.admin
      .pauseOrganization(id)
      .then((response) => {
        return response.data
      })
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async getOrganizationGroups(organizationId: number) {
    const client = await coreClient()
    return client.admin
      .getOrganizationGroups(organizationId)
      .then((data) => data.data)
      .catch((e) => {
        console.error(e)
        return []
      })
  },
  async createOrganizationGroup(organizationId: number, data: CreateGroupAttributes) {
    const client = await coreClient()
    return client.admin
      .createOrganizationGroup(organizationId, data)
      .then((data) => data.data)
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async updateOrganizationGroup(id: number, data: UpdateGroupAttributes) {
    const client = await coreClient()
    return client.admin
      .updateOrganizationGroup(id, data)
      .then((data) => data.data)
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async deleteOrganizationGroup(id: number) {
    const client = await coreClient()
    return client.admin
      .deleteOrganizationGroup(id)
      .then(() => true)
      .catch((e) => {
        console.error(e)
        return false
      })
  },
  async createOrganizationGroupUser(groupId: number, userIds: string[]) {
    const client = await coreClient()
    return client.admin
      .createOrganizationGroupUser(groupId, { user_ids: userIds })
      .then((data) => data.data)
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async deleteOrganizationGroupUser(groupId: number, userId: string) {
    const client = await coreClient()
    return client.admin
      .deleteOrganizationGroupUser(groupId, userId)
      .then(() => true)
      .catch((e) => {
        console.error(e)
        return false
      })
  },
  async createOrganizationGroupDefault(organizationId: number) {
    const client = await coreClient()
    return client.admin
      .createOrganizationGroupDefault(organizationId)
      .then((data) => data.data)
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async setOrganizationGroupExternal(organizationId: number) {
    const client = await coreClient()
    return client.admin
      .setExternal(organizationId)
      .then((data) => data.data)
      .catch((e) => {
        console.error(e)
        return null
      })
  },
  async getAllReportTemplates(): Promise<TemplateAIAttributes[]> {
    const client = await coreClient()
    const reports = await client.admin.getAllReportTemplates().then((data) => data.data)
    reports.sort((a, b) => a.name.localeCompare(b.name))
    return reports
  },
  async getReportTemplateById(templateId: number): Promise<TemplateAIAttributes> {
    const client = await coreClient()
    return client.admin.getReportTemplateById(templateId).then((data) => data.data)
  },
  async createReportTemplate(body: {
    name: string
    prompt: string
  }): Promise<TemplateAIAttributes> {
    const client = await coreClient()
    return client.admin.createReportTemplate(body).then((data) => data.data)
  },
  async updateReportTemplate(
    templateId: number,
    body: { name?: string; prompt?: string }
  ): Promise<TemplateAIAttributes> {
    const client = await coreClient()
    return client.admin.updateReportTemplate(templateId, body).then((data) => data.data)
  },
  async deleteReportTemplate(templateId: number): Promise<TemplateAIAttributes> {
    const client = await coreClient()
    return client.admin.deleteReportTemplate(templateId).then((data) => data.data)
  },
  async adminPreviewDataset(userId: string, payload: DatasetPayload) {
    const client = await coreClient()
    return client.admin.adminPreviewDataset({ payload, userId })
  },
  async adminExportDataset(
    userId: string,
    payload: DatasetPayload,
    name: string,
    format: 'csv' | 'jsonl'
  ) {
    const client = await coreClient()
    return client.admin.adminSendDatasetEmail({ userId, payload, name, format })
  },
  async createSso(
    organizationId: number,
    certificateFile: Blob,
    sso: {
      connectionName: string
      issuer: string
      signinUrl: string
      domains: string[]
    }
  ) {
    const client = await coreClient()
    return client.admin.createSso(organizationId, {
      ...sso,
      certificateFile,
      domains: sso.domains.join(',')
    })
  },
  async viewSso(organizationId: number) {
    const client = await coreClient()
    return client.admin.viewSso(organizationId)
  },
  async setSsoStatus(organizationId: number, enabled: boolean) {
    const client = await coreClient()
    return client.admin.setSsoStatus(organizationId, { enabled })
  }
}
